import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import type { WorkHours } from '@/types/api';

interface WorkHoursEditorProps {
  value: WorkHours[];
  onChange: (workHours: WorkHours[]) => void;
  required?: boolean;
}

const DAYS_OF_WEEK = [
  { dayOfWeek: 1, name: 'Monday' },
  { dayOfWeek: 2, name: 'Tuesday' },
  { dayOfWeek: 3, name: 'Wednesday' },
  { dayOfWeek: 4, name: 'Thursday' },
  { dayOfWeek: 5, name: 'Friday' },
  { dayOfWeek: 6, name: 'Saturday' },
  { dayOfWeek: 0, name: 'Sunday' },
];

export function WorkHoursEditor({ value, onChange, required = false }: WorkHoursEditorProps): JSX.Element {
  // Get the work hours for a specific day, or return defaults
  const getDayHours = (dayOfWeek: number): WorkHours => {
    const existing = value.find((wh) => wh.dayOfWeek === dayOfWeek);
    return existing ?? { dayOfWeek, isOpen: false, openTime: '09:00', closeTime: '18:00' };
  };

  // Update work hours for a specific day
  const updateDayHours = (dayOfWeek: number, updates: Partial<WorkHours>) => {
    const currentHours = getDayHours(dayOfWeek);
    const updatedHours = { ...currentHours, ...updates };

    // Replace or add the updated hours in the array
    const existingIndex = value.findIndex((wh) => wh.dayOfWeek === dayOfWeek);
    let newValue: WorkHours[];

    if (existingIndex >= 0) {
      newValue = [...value];
      newValue[existingIndex] = updatedHours;
    } else {
      newValue = [...value, updatedHours];
    }

    // Filter out days that are closed and have default times (to keep the data clean)
    // Only keep days that are open OR have non-default times
    onChange(newValue);
  };

  return (
    <div className="space-y-4">
      <Label className="flex items-center gap-2">
        Operating Hours
        {required && <span className="text-xs text-destructive">*</span>}
      </Label>
      <div className="rounded-lg border p-4">
        <div className="space-y-3">
          {DAYS_OF_WEEK.map((day) => {
            const hours = getDayHours(day.dayOfWeek);
            return (
              <div
                key={day.dayOfWeek}
                className="flex items-center gap-4 py-2 border-b last:border-b-0"
              >
                {/* Day name and toggle */}
                <div className="flex items-center gap-3 w-32">
                  <Switch
                    checked={hours.isOpen}
                    onCheckedChange={(checked) => {
                      updateDayHours(day.dayOfWeek, { isOpen: checked });
                    }}
                    data-testid={`work-hours-${day.name.toLowerCase()}-toggle`}
                  />
                  <span className={`text-sm font-medium ${hours.isOpen ? '' : 'text-muted-foreground'}`}>
                    {day.name}
                  </span>
                </div>

                {/* Time inputs - only shown when day is open */}
                {hours.isOpen ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={hours.openTime ?? '09:00'}
                      onChange={(e) => {
                        updateDayHours(day.dayOfWeek, { openTime: e.target.value });
                      }}
                      className="w-28"
                      data-testid={`work-hours-${day.name.toLowerCase()}-open`}
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      value={hours.closeTime ?? '18:00'}
                      onChange={(e) => {
                        updateDayHours(day.dayOfWeek, { closeTime: e.target.value });
                      }}
                      className="w-28"
                      data-testid={`work-hours-${day.name.toLowerCase()}-close`}
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Closed</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick actions */}
        <div className="mt-4 flex gap-2 pt-3 border-t">
          <button
            type="button"
            onClick={() => {
              // Set all weekdays (Mon-Fri) to open with default hours
              const weekdayHours: WorkHours[] = [1, 2, 3, 4, 5].map((dayOfWeek) => ({
                dayOfWeek,
                isOpen: true,
                openTime: '09:00',
                closeTime: '18:00',
              }));
              // Keep weekend as closed
              const weekendHours: WorkHours[] = [0, 6].map((dayOfWeek) => ({
                dayOfWeek,
                isOpen: false,
                openTime: '09:00',
                closeTime: '18:00',
              }));
              onChange([...weekdayHours, ...weekendHours]);
            }}
            className="text-xs text-primary hover:underline"
            data-testid="work-hours-set-weekdays"
          >
            Set Weekdays (Mon-Fri)
          </button>
          <span className="text-muted-foreground">|</span>
          <button
            type="button"
            onClick={() => {
              // Set all days to open with default hours
              const allDays: WorkHours[] = DAYS_OF_WEEK.map((day) => ({
                dayOfWeek: day.dayOfWeek,
                isOpen: true,
                openTime: '09:00',
                closeTime: '18:00',
              }));
              onChange(allDays);
            }}
            className="text-xs text-primary hover:underline"
            data-testid="work-hours-set-all"
          >
            Set All Days
          </button>
          <span className="text-muted-foreground">|</span>
          <button
            type="button"
            onClick={() => {
              // Clear all - set all days to closed
              onChange([]);
            }}
            className="text-xs text-muted-foreground hover:underline"
            data-testid="work-hours-clear"
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
}
